import { Grid } from '@mui/material';
import { useState } from 'react';
import { ICandidateSubmissionSearchFilterVM, IJob, IJobCandidateInteraction } from 'wisestep-api';
import { EmptyTeamsAndUsersIcon, WsTypoColors, WsTypography, WsTypoVariants } from 'wisestep-base';

import NoDataFoundCard from '../../../../components/noDataFoundCard';
import CardWrapper from '../../../../features/automatedEmailCampaignSettings/childRoutes/CardWrapper';
import { EmptyResponseCard } from '../../../../features/candidateLayout/components/childComponets';
import Resizable from '../../../../features/candidateLayout/components/childComponets/resizable/Resizable';
import useSimilarJobsFromJobSearch from '../../../../features/jobs/listing/hooks/useSimilarJobsFromJobSearch';
import NoSubmissionSVG from '../../../../images/NoSubmission.svg';
import { paginationSettingsStore } from '../../../../utils/PaginationSettingStore';
import { JobsListing } from '../../similarJobs/components';
import {
  getDefaultSimilarJobsMappingsQueryPayload,
  getDefaultSimilarJobsSubmissionsQueryPayload,
} from '../../utils/similarJobsHelper';

import SimilarCandidateListing from './SimilarCandidateListing';
import SimilarJobsHeader from './SimilarJobsHeader';

const SimilarJobs = ({ job }: { job: IJob }) => {
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());
  const [dragValue, setDragValue] = useState<boolean>(true);
  const { similarJobsData: similarJobs, loading } = useSimilarJobsFromJobSearch(job);
  const paginationData = paginationSettingsStore((state) => state.getPaginationSettings);
  const paginationSettings = paginationData();

  const [tabIndex, setTabIndex] = useState<number>(0);

  const [mappingQuery, setMappingQuery] = useState<IJobCandidateInteraction>(
    getDefaultSimilarJobsMappingsQueryPayload(Array.from(selectedJobIds))
  );
  const [submissionQuery, setSubmissionQuery] = useState<ICandidateSubmissionSearchFilterVM>(
    getDefaultSimilarJobsSubmissionsQueryPayload(
      {
        filterName: 'JOB_ID',
        values: Array.from(selectedJobIds),
      },
      Number(paginationSettings?.jobCrmPagination ?? 20)
    )
  );

  const updateMappingQuery = (query: IJobCandidateInteraction) => setMappingQuery(query);
  const updateSubmissionQuery = (query: ICandidateSubmissionSearchFilterVM) =>
    setSubmissionQuery(query);

  const handleTagChange = (val: number) => setTabIndex(val);

  const onSelectOrDeselectJob = (jobIds: string[], operation: 'Select' | 'DeSelect') => {
    const newSet = new Set(selectedJobIds);

    if (operation === 'Select') {
      jobIds.forEach((id) => newSet.add(id));
    } else {
      jobIds.forEach((id) => newSet.delete(id));
    }
    const updatedSubmissionQuery = { ...submissionQuery };
    const updatedMappingQuery = { ...mappingQuery };

    // updating Submission Query
    const others_filters = submissionQuery.filters.filter(
      (filter) => filter.filterName !== 'JOB_ID'
    );
    updatedSubmissionQuery.filters = [
      ...others_filters,
      { filterName: 'JOB_ID', values: Array.from(newSet) },
    ];
    updatedSubmissionQuery.page = 0;
    updateSubmissionQuery(updatedSubmissionQuery);

    // updating mapping Query
    updatedMappingQuery.jobIds = Array.from(newSet).join(',');
    updatedMappingQuery.page = 0;
    updateMappingQuery(updatedMappingQuery);

    setSelectedJobIds(newSet);
  };
  return (
    <Grid display="flex" flexDirection="column" rowGap={2}>
      <SimilarJobsHeader
        submissionQuery={submissionQuery}
        mappingQuery={mappingQuery}
        updateMappingsQuery={updateMappingQuery}
        updateSubmissionQuery={updateSubmissionQuery}
        filterType={tabIndex === 0 ? 'Submission' : 'Mappings'}
      />
      <Resizable
        leftPanel={
          !similarJobs.length ? (
            <CardWrapper>
              <EmptyResponseCard
                src={NoSubmissionSVG}
                subText=" No Similar Jobs Found."
                text="No jobs"
                sx={{ minHeight: 600 }}
              />
            </CardWrapper>
          ) : (
            <JobsListing
              jobs={similarJobs}
              selectedJobIds={selectedJobIds}
              onSelectOrDeselectJob={onSelectOrDeselectJob}
            />
          )
        }
        rightPanel={
          !similarJobs.length ? (
            <Grid container sx={{ width: '100%', height: '100%' }}>
              <NoDataFoundCard
                text={'Oops! No similar jobs found for the Job.'}
                icon={<EmptyTeamsAndUsersIcon />}
                subElement={
                  <Grid container p={4}>
                    <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                      Tip: To discover similar jobs to your current posting, we recommend providing
                      a clear job description, adding more relevant skills, and specifying the
                      accurate location. This will help you find candidates who are better matched
                      to the job more effectively.
                    </WsTypography>
                  </Grid>
                }
              />
            </Grid>
          ) : (
            <SimilarCandidateListing
              submissionQuery={submissionQuery}
              mappingQuery={mappingQuery}
              updateMappingsQuery={updateMappingQuery}
              updateSubmissionQuery={updateSubmissionQuery}
              tabIndex={tabIndex}
              handleTabChange={handleTagChange}
              job={job}
            />
          )
        }
        defaultSizes={[25, 75]}
        minSize="20%"
        maxSize="80%"
        isLoading={loading}
        pseudoDragValue={dragValue}
        setPseudoDragValue={setDragValue}
      />
    </Grid>
  );
};

export default SimilarJobs;
